/**
 * Trusted Types policy configuration for preventing DOM-based XSS.
 *
 * Trusted Types enforce secure handling of sensitive DOM sinks like innerHTML,
 * outerHTML, and document.write() by requiring developers to use secure policies.
 */

/// <reference types="trusted-types" />
import DOMPurify from 'dompurify';
import { createComponentLogger } from '@/lib/logging/frontend-logger';

const log = createComponentLogger('trusted-types');

/**
 * We define a 'default' policy to act as a global safety net for all
 * strings hitting DOM/Script sinks, particularly to support Zod's
 * internal validator generation.
 */

function registerDefaultPolicy(): void {
    window.trustedTypes!.createPolicy('default', {
        createHTML: (html: string) => DOMPurify.sanitize(html),
    });
}

/**
 * Main entry point for security initialization.
 * Should be called as early as possible in the application lifecycle.
 */
export function initializeTrustedTypes(): void {
    if (!window.trustedTypes) {
        log.warn('Trusted Types API not supported in this browser.');
        return;
    }

    // Check if the policy has already been created to avoid 'Duplicate Name' errors
    if (window.trustedTypes.defaultPolicy) {
        return;
    }

    registerDefaultPolicy();
}
